// async function subscribeToPush() {
//   // Public part of VAPID key, generation of that covered in README
//   // All subscription tokens associated with that key, so if you change it - you may lose old subscribers
//   const VAPID_PUBLIC_KEY =
//     'BAwUJxIa7mJZMqu78Tfy2Sb1BWnYiAatFCe1cxpnM-hxNtXjAwaNKz1QKLU8IYYhjUASOFzSvSnMgC00vfsU0IM'

import { Button } from '@/components/ui/button'
import { config } from '@/config/config'
import { useStore } from '@/hooks/store'
import { Bell, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

const PUSH_PROMPT_DISMISSED_KEY = 'pushPromptDismissed'

//   let swRegistration = await navigator.serviceWorker.getRegistration()
//   if (!swRegistration) return

//   let pushManager = swRegistration.pushManager
//   if (!isPushManagerActive(pushManager)) {
//     return
//   }
//   let subscriptionOptions = {
//     userVisibleOnly: true,
//     applicationServerKey: VAPID_PUBLIC_KEY,
//   }
//   try {
//     let subscription = await pushManager.subscribe(subscriptionOptions)
//     displaySubscriptionInfo(subscription)
//     // Here you can send fetch request with subscription data to your backend API for next push sends from there
//   } catch (error) {
//     document.getElementById('active_sub').style.display = 'block'
//     document.getElementById('active_sub').innerHTML =
//       'User denied push permission'
//   }
// }

type SubscriptionStatus =
  | 'unsupported'
  | 'prompt'
  | 'granted'
  | 'denied'
  | 'uninitialized'

async function getCurrentSubscription() {
  let swRegistration = await navigator.serviceWorker.getRegistration()
  if (!swRegistration) return null

  let pushManager = swRegistration.pushManager
  let subscription = await pushManager.getSubscription()
  return subscription
}

async function getLocalSubscriptionStatus() {
  let swRegistration = await navigator.serviceWorker.getRegistration()

  if (!swRegistration) return 'unsupported'

  let pushManager = swRegistration.pushManager

  let permissionState = await pushManager.permissionState({
    userVisibleOnly: true,
  })
  switch (permissionState) {
    case 'prompt':
      return 'prompt'
    case 'granted':
      const info = await pushManager.getSubscription()
      console.log('subscription info', JSON.stringify(info))
      return 'granted'
    case 'denied':
      console.log('denied')
      return 'denied'
  }
}

// Safari/iOS requires applicationServerKey as a BufferSource, not the raw
// base64url string that Chrome accepts. Passing the string makes
// pushManager.subscribe() throw on iOS, so the Enable button did nothing.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }

  return outputArray
}

async function subscribeToPush() {
  // Public part of VAPID key, generation of that covered in README
  // All subscription tokens associated with that key, so if you change it - you may lose old subscribers
  const VAPID_PUBLIC_KEY = config.vapidKey
  if (!VAPID_PUBLIC_KEY) return null

  let swRegistration = await navigator.serviceWorker.getRegistration()
  if (!swRegistration) return null

  let pushManager = swRegistration.pushManager

  // Reuse an existing subscription to avoid InvalidStateError on re-subscribe
  const existingSubscription = await pushManager.getSubscription()
  if (existingSubscription) {
    return existingSubscription
  }

  const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  const subscription = await pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey as BufferSource,
  })

  return subscription
}

function resolveClientId() {
  let clientId = localStorage.getItem('clientId')

  if (clientId) return clientId

  clientId = crypto.randomUUID()

  localStorage.setItem('clientId', clientId)

  return clientId
}

export function Subscription() {
  const store = useStore()
  const clientId = resolveClientId()
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>('uninitialized')
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY) === '1'
  )

  const dismiss = useCallback(() => {
    localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, '1')
    setDismissed(true)
  }, [])

  useEffect(() => {
    store.provider
      .getPushSubscription(clientId)
      .then((subscription) => {
        console.log('stored subscription', subscription)
      })
      .catch(console.error)
    getLocalSubscriptionStatus().then(async (status) => {
      setSubscriptionStatus(status)
      if (status === 'granted') {
        const subscription = await getCurrentSubscription()
        if (subscription) {
          store.provider.updatePushSubscription(
            clientId,
            JSON.stringify(subscription)
          )
        }
      }
    })
  }, [clientId])

  const subscribe = useCallback(async () => {
    try {
      const subscription = await subscribeToPush()
      if (!subscription) {
        toast.error('Notifications are not available on this device.')
        return
      }
      store.provider.updatePushSubscription(
        clientId,
        JSON.stringify(subscription)
      )
      setSubscriptionStatus('granted')
    } catch (error) {
      console.error('Push subscription failed', error)
      toast.error('Could not enable notifications. Please try again.')
    }
  }, [clientId])

  if (subscriptionStatus !== 'prompt' || dismissed) return null

  return (
    <div className="mb-6 flex items-center gap-3 rounded-2xl border bg-card px-4 py-3 shadow-card">
      <div className="p-2 rounded-xl bg-primary/10 flex-shrink-0">
        <Bell className="w-5 h-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-tight">Turn on notifications</p>
        <p className="text-sm text-muted-foreground">
          Get a nudge when your checklists are due.
        </p>
      </div>
      <Button size="sm" onClick={subscribe} className="flex-shrink-0">
        Enable
      </Button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="flex-shrink-0 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
